import { useRef } from 'react';
import { useLocalizedSpread } from '../../i18n/useLocalized';
import { useReadingFlow, hasPassed, hasReached } from '../flow/useReadingFlow';
import { useFlowScroll } from '../flow/useFlowScroll';
import { useCardFlight } from '../flow/useCardFlight';
import FlowStep from '../flow/FlowStep';
import DeckSection from './DeckSection';
import SpreadActions from './SpreadActions';
import CardFanSelector from '../CardFanSelector';
import ReadingDisplay from '../ReadingDisplay';
import { ReadingSession } from '../../voice/ReadingSession';
import ReadingHalo from '../../voice/ReadingHalo';
import SingleCardSpread from './SingleCardSpread';
import ThreeCardSpread from './ThreeCardSpread';
import CelticCrossSpread from './CelticCrossSpread';
import RelationshipSpread from './RelationshipSpread';
import CareerSpread from './CareerSpread';

// Each spread's layout, and the class its stylesheet scopes the header
// and the cards under
const LAYOUTS = {
  'one-card': { Layout: SingleCardSpread, className: 'single-card-spread' },
  'three-card': { Layout: ThreeCardSpread, className: 'three-card-spread' },
  'celtic-cross': { Layout: CelticCrossSpread, className: 'celtic-cross-spread' },
  relationship: { Layout: RelationshipSpread, className: 'relationship-spread' },
  career: { Layout: CareerSpread, className: 'career-spread' }
};

/**
 * One reading of `spread`, as steps stacked down the page: shuffle the
 * deck, pick the cards, turn them, read them. Each step appears under the
 * last and is locked once the reading moves past it, so the reader can
 * scroll back through it but not change it.
 */
function SpreadFlow({ spread: rawSpread, onNewReading }) {
  const spread = useLocalizedSpread(rawSpread);
  const numCards = rawSpread.positions.length;
  const { Layout, className } = LAYOUTS[rawSpread.id];

  const flow = useReadingFlow(numCards);
  const { phase, deck, isShuffling, hasShuffled, drawn, revealed, allRevealed } = flow;
  const reached = (step) => hasReached(phase, step);
  const passed = (step) => hasPassed(phase, step);

  const steps = {
    shuffle: useRef(null),
    pick: useRef(null),
    reveal: useRef(null),
    reading: useRef(null)
  };
  useFlowScroll(phase, steps);
  // Where the deck sits, so the fan can pick it up from there
  const deckRef = useRef(null);

  // Each pick flies from its place in the fan to its place in the spread
  const { landingRef, launchFrom } = useCardFlight(drawn.length);
  const pick = (card, fanRect) => {
    launchFrom(fanRect);
    flow.pick(card);
  };

  return (
    // `drawn` is only handed over once asked for, which starts the reading
    <ReadingSession cards={reached('reading') ? drawn : null} spread={rawSpread}>
      <div className={className}>
        <FlowStep name="shuffle" ref={steps.shuffle} locked={passed('shuffle')}>
          <div className="spread-header">
            <h2>{spread.name}</h2>
            <p>{spread.description}</p>
          </div>
          <DeckSection
            isShuffling={isShuffling}
            hasShuffled={hasShuffled}
            onShuffle={flow.shuffle}
            onDraw={flow.draw}
            numCards={numCards}
            deckRef={deckRef}
            locked={passed('shuffle')}
          />
        </FlowStep>

        {reached('pick') && (
          <FlowStep name="pick" ref={steps.pick} locked={passed('pick')}>
            <CardFanSelector
              deck={deck}
              numCards={numCards}
              pickedCount={drawn.length}
              deckRef={deckRef}
              onPick={pick}
              open={phase === 'pick'}
            />
          </FlowStep>
        )}

        {/* The spread fills while the reader picks: each card taken from the
            fan lands straight in its place here, and is the same card they
            then turn and have read — there is only ever the one */}
        {reached('pick') && (
          <FlowStep name="reveal" ref={steps.reveal} locked={passed('reveal')}>
            <ReadingHalo />
            <div ref={landingRef} className={`dealt-cards${phase === 'pick' ? ' is-filling' : ''}`}>
              <Layout
                spread={spread}
                cards={drawn}
                revealed={revealed}
                onReveal={flow.reveal}
              />
            </div>
            {reached('reveal') && (
              <SpreadActions
                allRevealed={allRevealed}
                drawnCardsLength={drawn.length}
                revealedCardsLength={revealed.length}
                onRevealAll={flow.revealAll}
                onGetReading={flow.requestReading}
                onNewReading={onNewReading}
                locked={passed('reveal')}
              />
            )}
          </FlowStep>
        )}
      </div>

      {reached('reading') && (
        <FlowStep name="reading" ref={steps.reading}>
          <ReadingDisplay onNewReading={onNewReading} />
        </FlowStep>
      )}
    </ReadingSession>
  );
}

export default SpreadFlow;
